import { mapUnloRoute } from '@dfds-pax/utils'

import { Addon, SelectedAddon } from '@/app/[locale]/[ferry]/extras/_components/interface'

import { TrackingData } from './_actions/getTrackingData'
import { mapToTrackingProduct, ProductResult, trackAddToCartEcommerce, trackRemoveFromCartEcommerce } from './_ecommerce/cart'
import { trackAddonAddedSegment, trackAddonRemovalSegment, trackAddonRemovedSegment } from './_segment/cart'
import { AddonRemovalDeparture, DepartureTrackingContext } from './_segment/cartPayload'

export const trackAddonQuantityChange = (
  isReturnLeg: boolean,
  addon: Addon,
  previousQuantity: number,
  newQuantity: number,
  trackingData: TrackingData
): void => {
  if (newQuantity > previousQuantity) {
    trackAddAddon(isReturnLeg, addon, newQuantity - previousQuantity, trackingData)
  } else if (newQuantity < previousQuantity) {
    trackRemoveAddon(isReturnLeg, addon, previousQuantity - newQuantity, trackingData)
  }
}

const trackAddAddon = (isReturnLeg: boolean, addon: Addon, quantity: number, trackingData: TrackingData): void => {
  const productResult = getAddonProductFromTracking(isReturnLeg, { ...addon, selectedQuantity: quantity }, trackingData)
  if (!productResult) return
  trackAddToCartEcommerce(productResult.product, productResult.currencyCode)
  trackAddonAddedSegment(isReturnLeg ? 'home' : 'out', trackingData, productResult.product, productResult.currencyCode)
}

const trackRemoveAddon = (isReturnLeg: boolean, addon: Addon, quantity: number, trackingData: TrackingData): void => {
  const productResult = getAddonProductFromTracking(isReturnLeg, { ...addon, selectedQuantity: quantity }, trackingData)
  if (!productResult) return
  trackRemoveFromCartEcommerce(productResult.product)
  trackAddonRemovedSegment(isReturnLeg ? 'home' : 'out', trackingData, productResult.product, productResult.currencyCode)
}

const getAddonProductFromTracking = (isReturnLeg: boolean, addon: SelectedAddon, trackingData: TrackingData): ProductResult | null => {
  const currentDeparture = isReturnLeg ? trackingData.departures[1] : trackingData.departures[0]
  const currentRoute = currentDeparture && mapUnloRoute(`${currentDeparture.departurePort}-${currentDeparture.arrivalPort}`)

  if (currentDeparture && currentRoute && addon && addon.selectedQuantity) {
    // Both legs are priced with the booking-level (outbound) offer code, so fall back to it when this leg has none.
    const offerCode = currentDeparture.offerCode ?? trackingData.departures.find((departure) => departure.offerCode)?.offerCode
    const product = mapToTrackingProduct(
      addon.addonCode,
      currentRoute,
      currentDeparture.departureDate,
      currentDeparture.departureTime ?? '',
      addon.priceInfo.offerPrice !== undefined && addon.priceInfo.offerPrice !== null && offerCode ? offerCode : 'UNDEFINED',
      addon.priceInfo.offerPrice ?? addon.priceInfo.basePrice,
      addon.selectedQuantity,
      currentDeparture.productCode ?? '',
      'SeabookExtra',
      isReturnLeg
    )
    return { currencyCode: addon.priceInfo.currency, product: product }
  }
  return null
}

export interface AddonRemoval {
  addonCode: string
  basePrice: number
  offerPrice: number | null
  quantity: number
}

// Basket-sourced add-on removal for yourbooking departure-change flows (no trackingData available there).
export const trackRemoveAddons = (
  isReturnLeg: boolean,
  departure: AddonRemovalDeparture,
  addons: AddonRemoval[],
  context: DepartureTrackingContext
): void => {
  const leg = isReturnLeg ? 'home' : 'out'
  const route = mapUnloRoute(`${departure.departurePort}-${departure.arrivalPort}`)

  addons.forEach((addon) => {
    if (!addon.quantity) return
    const product = mapToTrackingProduct(
      addon.addonCode,
      route,
      departure.departureDate,
      departure.departureTime,
      addon.offerPrice !== null && departure.offerCode ? departure.offerCode : 'UNDEFINED',
      addon.offerPrice ?? addon.basePrice,
      addon.quantity,
      departure.productCode,
      'SeabookExtra',
      isReturnLeg
    )
    trackRemoveFromCartEcommerce(product)
    trackAddonRemovalSegment(leg, departure, product, context)
  })
}
